import { LoadingOutlined } from "@ant-design/icons";
import {
    Alert,
    Button,
    Col,
    Form,
    Modal,
    Row,
    Select,
    Spin
} from "antd";
import { useEffect, useState } from "react";
import PatientDocumentServices from "../../services/PatientDocumentServices";
import UserServices from "../../services/UserServices";

export const validateFile = (file) => {
  if (!file) {
    return { valid: false, error: null };
  }

  const fileName = file.name || "";
  const extension = fileName.toLowerCase().endsWith(".pdf");
  const mimeType = file.type === "application/pdf";

  if (!extension || !mimeType) {
    return { valid: false, error: "Solo se aceptan archivos PDF" };
  }

  if (file.size > 10485760) {
    return { valid: false, error: "El tamaño máximo permitido es 10 MB" };
  }

  return { valid: true, error: null };
};

const UploadDocumentModal = ({
  username,
  visible,
  mode,
  document,
  onComplete,
  onCancel,
}) => {
  const [loading, setLoading] = useState(false);
  const [values, setValues] = useState({
    subscription_id: "",
    tag_id: "",
    file: null,
  });
  const [subscriptions, setSubscriptions] = useState([]);
  const [tags, setTags] = useState([]);
  const [error, setError] = useState("");
  const [fileError, setFileError] = useState("");

  useEffect(() => {
    if (visible) {
      getSubscriptions();
      getTags();
    }
  }, [visible]);

  useEffect(() => {
    if (visible && mode === "replace" && document) {
      setValues((v) => ({
        ...v,
        subscription_id: document.subscription_id || "",
        tag_id: document.tag ? document.tag.id : "",
      }));
    }
  }, [visible, mode, document]);

  const getSubscriptions = async () => {
    try {
      const response = await UserServices.getSubscription(username);
      if (response.status >= 200 && response.status < 300) {
        const subs = response.data.subscriptions.map((i) => ({
          value: i.id,
          label: i.name,
        }));
        setSubscriptions(subs);
        // Auto-select if only one subscription
        if (subs.length === 1) {
          setValues((v) => ({ ...v, subscription_id: subs[0].value }));
        }
      }
    } catch (err) {
      console.log(err);
    }
  };

  const getTags = async () => {
    try {
      const response = await PatientDocumentServices.getTags(username);
      if (response.status >= 200 && response.status < 300) {
        setTags(
          (response.data.tags || []).map((t) => ({
            value: t.id,
            label: t.name,
          }))
        );
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) {
      setValues((v) => ({ ...v, file: null }));
      return;
    }

    const validation = validateFile(file);
    if (!validation.valid) {
      setFileError(validation.error);
      setValues((v) => ({ ...v, file: null }));
      e.target.value = "";
      return;
    }

    setFileError("");
    setValues((v) => ({ ...v, file }));
  };

  const handleCancel = () => {
    resetForm();
    if (onCancel) {
      onCancel();
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError("");

    try {
      const formData = new FormData();
      formData.append("patient", username);
      formData.append("subscription_id", values.subscription_id);
      formData.append("tag_id", values.tag_id);
      formData.append("file", values.file);

      const response = await PatientDocumentServices.uploadDocument(formData);

      if (response.status === 201) {
        resetForm();
        if (onComplete) {
          onComplete();
        }
      }
    } catch (err) {
      if (err.response) {
        if (err.response.status === 400) {
          setError(err.response.data.message || "Error de validación");
        } else if (err.response.status >= 500) {
          setError("Ocurrió un error interno. Reintente más tarde.");
        }
      } else {
        setError("Error de conexión. Verifique su red.");
      }
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setValues({ subscription_id: "", tag_id: "", file: null });
    setError("");
    setFileError("");
  };

  const isSubmitDisabled = () => {
    return !values.subscription_id || !values.tag_id || !values.file;
  };

  return (
    <Modal
      open={visible}
      title={mode === "replace" ? "Reemplazar documento" : "Nuevo documento PDF"}
      onCancel={handleCancel}
      footer={false}
      data-testid="upload-document-modal"
      destroyOnClose
    >
      <div data-testid="upload-document-modal">
        {error && (
          <Alert
            message={error}
            type="error"
            className="mb-6"
            closable
            onClose={() => setError("")}
          />
        )}
        <Form autoComplete="off" className="w-full">
          <Form.Item>
            <Select
              data-testid="upload-document-select-subscription"
              placeholder="Seleccione suscripción"
              onChange={(value) => {
                setValues((v) => ({ ...v, subscription_id: value }));
              }}
              value={values.subscription_id || undefined}
            >
              {subscriptions.map((item) => (
                <Select.Option key={item.value} value={item.value}>
                  {item.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item>
            <Select
              data-testid="upload-document-select-tag"
              placeholder="Seleccione tag"
              onChange={(value) => {
                setValues((v) => ({ ...v, tag_id: value }));
              }}
              value={values.tag_id || undefined}
            >
              {tags.map((item) => (
                <Select.Option key={item.value} value={item.value}>
                  {item.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item>
            <input
              type="file"
              accept="application/pdf"
              data-testid="upload-document-input-file"
              onChange={handleFileChange}
            />
            {fileError && (
              <div
                data-testid="upload-document-error-file"
                className="text-red-600 pt-2 text-xs"
              >
                {fileError}
              </div>
            )}
          </Form.Item>
          <Form.Item>
            <Row align="end" gutter={16}>
              <Col>
                <Button
                  data-testid="upload-document-btn-cancel"
                  disabled={loading}
                  onClick={handleCancel}
                >
                  Cancelar
                </Button>
              </Col>
              <Col>
                <Button
                  data-testid="upload-document-btn-submit"
                  disabled={isSubmitDisabled() || loading}
                  onClick={handleSubmit}
                  type="primary"
                  className="bg-custom_primary border-custom_primary"
                >
                  {loading ? (
                    <Spin
                      indicator={
                        <LoadingOutlined style={{ color: "#666666" }} />
                      }
                    />
                  ) : (
                    "Cargar"
                  )}
                </Button>
              </Col>
            </Row>
          </Form.Item>
        </Form>
      </div>
    </Modal>
  );
};

export default UploadDocumentModal;
